import { useQuery } from '@tanstack/react-query';
import { createClient } from '@/lib/supabase/client';
import { queryKeys } from '@/lib/queries/queryKeys';

/**
 * Fetches the current server timestamp via get_server_now() RPC.
 * Use this instead of `new Date()` for time-sensitive comparisons
 * (test/survey availability, deadlines) – the browser clock may be
 * wrong (e.g. Viber in-app WebView, misconfigured phone timezone).
 *
 * Returns a Date object, or falls back to browser time if the RPC fails.
 */
export function useServerNow() {
  const supabase = createClient();

  const { data: serverNow } = useQuery<Date>({
    queryKey: queryKeys.serverNow,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_server_now');
      if (error || !data) return new Date();
      return new Date(data);
    },
    staleTime: 60 * 1000, // refresh every 60s at most
  });

  return serverNow ?? new Date(); // fallback to browser time while loading
}
